import React from "react";
import { Box, Chip, LinearProgress, Typography } from "@mui/material";
import { getPointsBalance, getStatusPoints, getTierLabel } from "../../utils/loyaltyDisplay";
import {
    formatMoney,
    historyEventToLastChange,
    isPurchasesMode,
    qualificationModeLabel,
} from "../../utils/loyaltyQualification";
import LastChangeChip from "./LastChangeChip";

function KpiCard({ title, value }) {
    return (
        <Box
            textAlign="center"
            p={2}
            bgcolor="#fafbfd"
            borderRadius={2}
            border="1px solid #e8ecf1"
        >
            <Typography fontSize={11} color="text.secondary" fontWeight={600} mb={0.5} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="h6" fontWeight={800} color="#1a1a2e">
                {value}
            </Typography>
        </Box>
    );
}

function DualProgress({ current, remaining, nextLabel, unit, color }) {
    const nextThreshold = remaining == null ? null : Number(current || 0) + Number(remaining || 0);
    const pct = nextThreshold > 0
        ? Math.min(100, (Number(current || 0) / nextThreshold) * 100)
        : 100;
    return (
        <Box mb={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} gap={1}>
                <Typography fontSize={12} color="text.secondary">
                    {nextLabel ? `Vers ${nextLabel}` : "Palier max atteint"}
                    {remaining != null && nextLabel ? ` — ${formatMoney(remaining)} ${unit}`.trim() : ""}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={Number.isFinite(pct) ? pct : 100}
                sx={{ height: 8, borderRadius: 4, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { bgcolor: color } }}
            />
        </Box>
    );
}

function ModeTierThresholds({ tier, purchases }) {
    if (!tier) return null;
    if (purchases) {
        return (
            <Typography fontSize={12} color="text.secondary">
                Seuils : {formatMoney(tier.minPurchaseCount ?? tier.min_purchase_count ?? 0)} achat(s)
                {" · "}
                {formatMoney(tier.minPurchaseAmount ?? tier.min_purchase_amount ?? 0)} montant
            </Typography>
        );
    }
    return (
        <Typography fontSize={12} color="text.secondary">
            Seuil : {formatMoney(tier.minStatusPoints ?? tier.min_status_points ?? 0)} SP
        </Typography>
    );
}

export default function LoyaltyStatusOverview({
    loyalty,
    customer,
    accentColor = "#3cb0d6",
}) {
    if (!loyalty && !customer) return null;
    const purchases = isPurchasesMode(loyalty?.qualificationMode);
    const nextName = loyalty?.nextTier?.name;
    const currentName = getTierLabel(loyalty, customer);
    const lastChangeKey = historyEventToLastChange({ lastChange: loyalty?.lastChange }) || loyalty?.lastChange;

    return (
        <Box>
            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
                <Chip
                    size="small"
                    label={`Mode ${qualificationModeLabel(loyalty?.qualificationMode)}`}
                    sx={{ fontWeight: 700, bgcolor: purchases ? "#ffedd5" : "#e0f2fe", color: purchases ? "#9a3412" : "#075985" }}
                />
                {lastChangeKey != null && lastChangeKey !== "" && (
                    <LastChangeChip value={lastChangeKey} />
                )}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 2,
                    mb: 2,
                }}
            >
                <KpiCard title="Niveau" value={currentName} />
                {purchases ? (
                    <>
                        <KpiCard title="Achats" value={formatMoney(loyalty?.purchaseCount ?? 0)} />
                        <KpiCard title="Montant total" value={formatMoney(loyalty?.purchaseAmountTotal ?? 0)} />
                    </>
                ) : (
                    <>
                        <KpiCard title="Points de statut" value={getStatusPoints(loyalty, customer)} />
                        <KpiCard title="Solde points" value={getPointsBalance(loyalty, customer)} />
                    </>
                )}
            </Box>

            {loyalty?.currentTier && (
                <Box p={2} bgcolor="#f8f9ff" borderRadius={2} border="1px solid #e8ecf1" mb={2}>
                    <Typography fontSize={13} fontWeight={600} color="#334155" mb={0.5}>
                        Progression — niveau actuel : <strong>{loyalty.currentTier.name}</strong>
                        {nextName ? <> → <strong>{nextName}</strong></> : " (palier le plus haut)"}
                    </Typography>
                    <Box mb={1.5} display="flex" flexDirection="column" gap={0.5}>
                        <ModeTierThresholds tier={loyalty.currentTier} purchases={purchases} />
                        {loyalty.nextTier && (
                            <ModeTierThresholds tier={loyalty.nextTier} purchases={purchases} />
                        )}
                    </Box>
                    {purchases ? (
                        <>
                            <DualProgress
                                current={loyalty.purchaseCount}
                                remaining={loyalty.purchasesToNextTier}
                                nextLabel={nextName}
                                unit="achat(s)"
                                color={accentColor}
                            />
                            <DualProgress
                                current={loyalty.purchaseAmountTotal}
                                remaining={loyalty.amountToNextTier}
                                nextLabel={nextName}
                                unit=""
                                color="#ea580c"
                            />
                        </>
                    ) : (
                        <DualProgress
                            current={getStatusPoints(loyalty, customer)}
                            remaining={loyalty.pointsToNextTier}
                            nextLabel={nextName}
                            unit="SP"
                            color={accentColor}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}
